import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Signature from "../Signature";

/**
 * About — "Editorial Portrait Story"
 * Asymmetric two-column: tall portrait with ambient lantern glow + long-form
 * narrative, milestone strip, pull-quote, CTA.
 */

const PORTRAIT =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_hero-design-6/artifacts/r9yjgkhu_Thoughtful%20scholar%20with%20an%20open%20book.png";

const MILESTONES = [
  { v: "20+", l: "Years in Education" },
  { v: "3",   l: "Continents Served" },
  { v: "500+",l: "Students Mentored" },
  { v: "50+", l: "Talks & Khutbahs" },
];

const AboutEditorialSection = ({ section }) => {
  return (
    <section
      data-testid={`featured-section-${section.id}`}
      className="relative overflow-hidden bg-ink"
    >
      {/* Layered ambient depth */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse 45% 60% at 22% 35%, rgba(201,169,97,0.08), transparent 60%), radial-gradient(ellipse 60% 55% at 95% 90%, rgba(74,143,229,0.10), transparent 65%)",
        }}
      />
      {/* Soft top vignette */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute top-0 inset-x-0 h-40"
        style={{ background: "linear-gradient(to bottom, rgba(6,19,37,0.85), transparent)" }}
      />

      <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-16 lg:py-22">
        {/* Eyebrow */}
        <div className="flex items-center gap-4 mb-12">
          <span className="text-[11px] tracking-[0.34em] uppercase text-azure-soft">
            {section.eyebrow}
          </span>
          <span className="h-px w-10 bg-azure-soft/50" />
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Portrait column */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={PORTRAIT}
                alt="AI-generated portrait of Iyare Omoruyi studying an open book at a desk."
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
                style={{ objectPosition: "center 35%" }}
              />
              {/* cinematic vignette */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse 80% 70% at 50% 50%, transparent 50%, rgba(6,19,37,0.85) 100%)",
                }}
              />
              {/* warm lantern glow */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle 320px at 15% 20%, rgba(201,169,97,0.12), transparent 60%)",
                }}
              />
              {/* thin azure frame */}
              <div className="absolute inset-0 border border-azure-soft/15" />
            </div>

            {/* Floating caption */}
            <div className="mt-7 pl-1">
              <Signature className="h-14" />
              <div className="mt-3 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
                In study and reflection.
              </div>
            </div>
          </div>

          {/* Narrative column */}
          <div className="lg:col-span-7 lg:pt-2">
            <h2 className="font-serif text-white text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineA}
            </h2>
            <h2 className="font-serif italic text-azure-soft text-[42px] sm:text-[54px] lg:text-[64px] leading-[1.02] tracking-[-0.02em]">
              {section.headlineB}
            </h2>

            <div className="mt-12 grid sm:grid-cols-12 gap-10 sm:gap-12">
              <div className="sm:col-span-7 space-y-6 text-white/70 text-[15.5px] leading-[1.95]">
                <p>
                  Iyare Omoruyi has spent over two decades in education —
                  quietly insisting that teaching must do more than transfer
                  information. It must cultivate character, awaken purpose, and
                  help form the human being.
                </p>
                <p>
                  Rooted in the classical Islamic tradition of the{" "}
                  <em className="text-azure-soft not-italic">Murabbi</em>, his
                  work as an educator, mentor, and speaker centers on the belief
                  that true teaching shapes the soul before it shapes
                  performance.
                </p>
              </div>

              {/* Pull quote */}
              <div className="sm:col-span-5 relative pl-7 border-l border-azure-soft/30">
                <span
                  aria-hidden="true"
                  className="absolute -left-2 -top-3 font-serif text-azure-soft/70 text-[64px] leading-none"
                >
                  “
                </span>
                <p className="font-serif italic text-white/90 text-[19px] leading-[1.55] tracking-[-0.005em]">
                  The teachers who shaped me did not simply teach lessons.
                  <span className="text-azure-soft"> They taught me how to become a person.</span>
                </p>
                <div className="mt-5 text-[10.5px] tracking-[0.34em] uppercase text-white/45">
                  On formation
                </div>
              </div>
            </div>

            {/* Milestones strip */}
            <div className="mt-14 pt-10 border-t border-white/10 grid grid-cols-2 sm:grid-cols-4 gap-y-8">
              {MILESTONES.map((m) => (
                <div key={m.l}>
                  <div className="font-serif text-white text-[36px] lg:text-[44px] leading-none tracking-[-0.02em]">
                    {m.v}
                  </div>
                  <div className="mt-3 text-[10.5px] tracking-[0.34em] uppercase text-azure-soft/80">
                    {m.l}
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="mt-14">
              <Link
                to={section.viewAllHref}
                data-testid={`featured-view-all-${section.id}`}
                className="group inline-flex items-center gap-3 text-[11px] tracking-[0.34em] uppercase text-azure-soft hover:text-azure border-b border-azure-soft/60 hover:border-azure pb-2.5 transition-colors"
              >
                Read his full story
                <ArrowRight className="w-3.5 h-3.5 transition-transform duration-400 group-hover:translate-x-1.5" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutEditorialSection;
